import { t } from "@/i18n";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Printer, Sparkles } from "lucide-react";
import { API, api, formatApiError, printImage } from "@/lib/api";
import { Logo, Loader } from "@/components/Loader";
import { StoryReader } from "@/components/StoryDialog";

export default function SharedArtwork() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/share/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(formatApiError(e)));
  }, [token]);

  const imageUrl = `${API}/share/${token}/image`;

  return (
    <div className="min-h-[100dvh] bg-[#FFF9F2] dot-grid flex flex-col" data-testid="shared-artwork-page">
      <header className="h-20 px-4 sm:px-6 flex items-center justify-between bg-white/90 backdrop-blur-md border-b-2 border-slate-100">
        <Link to="/" data-testid="shared-brand-link"><Logo small /></Link>
        <Link to="/signup" className="btn-pop btn-red !py-2.5 !px-5 text-sm" data-testid="shared-cta-button"><Sparkles className="w-4 h-4" /> {t("make_your_own")}</Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        {error ? (
          <div className="card-soft p-10 text-center space-y-3 max-w-md" data-testid="shared-error">
            <p className="font-display text-2xl font-semibold">{t("oops")}</p>
            <p className="text-slate-600">{error}</p>
            <Link to="/" className="btn-pop btn-ink inline-flex">{t("go_to_paintipop")}</Link>
          </div>
        ) : !info ? (
          <Loader label={t("opening_the_picture")} />
        ) : (
          <div className="w-full max-w-2xl space-y-6 rise">
            <div className="text-center space-y-1">
              <p className="font-bold text-[#FF5252] uppercase tracking-wider text-sm">{t("a_masterpiece_by")}</p>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="shared-artist-name">{info.artist}</h1>
              <p className="text-slate-600 text-base sm:text-lg" data-testid="shared-page-title">
                "{info.page_title}" · {new Date(info.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
            <div className="bg-white rounded-3xl border-4 border-[#1E293B] shadow-[8px_10px_0_0_#1E293B] p-3 -rotate-1 hover:rotate-0 transition-transform duration-300">
              <img src={imageUrl} alt={`${info.page_title} coloured by ${info.artist}`} className="w-full rounded-2xl" data-testid="shared-artwork-image" />
            </div>
            <div className="flex justify-center gap-3 flex-wrap">
              <button onClick={() => printImage(imageUrl, `${info.page_title} by ${info.artist}`)} className="btn-pop btn-red" data-testid="shared-print-button">
                <Printer className="w-5 h-5" /> {t("print_picture")}
              </button>
              <a href={imageUrl} download={`${info.artist}-${info.page_title}.png`.replace(/\s+/g, "-").toLowerCase()} className="btn-pop btn-white" data-testid="shared-download-button">
                <Download className="w-5 h-5" /> {t("download_picture")}
              </a>
            </div>
            {info.story && (
              <div className="card-soft p-6 sm:p-8" data-testid="shared-story">
                <p className="text-sm font-bold uppercase tracking-wider text-[#FF5252] mb-3">{t("story_time")}</p>
                <StoryReader story={info.story} imageUrl={imageUrl} audioUrl={`${API}/share/${token}/story/audio`} artist={info.artist} />
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
